/** @format */

// Pure helpers powering ProPilot's usage-aware nudges: lifecycle staging,
// personalised greetings, milestone detection and wait-state narration.
// No React, no MUI — keep this file trivially testable.

const DAY_MS = 24 * 60 * 60 * 1000;

// Question-count milestones worth celebrating. Crossing one mid-session
// triggers a subtle snackbar (see index.js).
export const MILESTONES = [1, 5, 10, 25, 50, 100];

const daysBetween = (isoDate, now = Date.now()) => {
  if (!isoDate) return null;
  const then = new Date(isoDate).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now - then) / DAY_MS));
};

// ── Lifecycle staging ─────────────────────────────────────────────────────────
//
// Maps the analytics stats (GET /api/proship/analytics/me) onto a coarse
// user-lifecycle stage so the landing screen can speak to where the user
// actually is: brand new, finding their feet, habitual, or power user.
export function deriveLifecycle(stats) {
  const totalQuestions = stats?.total_questions ?? 0;
  let stage;
  if (totalQuestions === 0) stage = "new";
  else if (totalQuestions <= 5) stage = "exploring";
  else if (totalQuestions <= 25) stage = "regular";
  else stage = "power";

  return {
    stage,
    totalQuestions,
    daysSinceFirstSeen: daysBetween(stats?.first_seen),
    returningGapDays: daysBetween(stats?.last_active),
  };
}

export function nextMilestone(total) {
  const target = MILESTONES.find((m) => m > total);
  return target ? { target, remaining: target - total } : null;
}

// Returns the milestone crossed between two running totals, or null.
// (If several were crossed at once, celebrate the biggest.)
export function checkMilestoneCrossed(prevTotal, newTotal) {
  const crossed = MILESTONES.filter((m) => prevTotal < m && newTotal >= m);
  return crossed.length ? crossed[crossed.length - 1] : null;
}

export function milestoneMessage(milestone) {
  switch (milestone) {
    case 1:
      return "🎉 First question asked — welcome aboard!";
    case 5:
      return "🙌 That's 5 questions — you're getting the hang of ProPilot!";
    case 10:
      return "🎉 That's your 10th question — you're officially a regular!";
    case 25:
      return "⚡ 25 questions — welcome to the power-user club!";
    case 50:
      return "🏆 50 questions! Your shipping decisions thank you.";
    case 100:
      return "💯 100 questions — ProPilot legend status unlocked!";
    default:
      return `🎉 ${milestone} questions and counting!`;
  }
}

// ── Personalised greeting ─────────────────────────────────────────────────────

export const timeOfDay = (hour) => {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

// Stage-aware hero copy for the landing screen. `topTopicLabel` is the
// humanised top intent (e.g. "NDR Analysis") when available.
export function greetingFor({ lifecycle, firstName, hour, topTopicLabel }) {
  const hi = timeOfDay(hour ?? new Date().getHours());
  const name = firstName ? `, ${firstName}` : "";
  const { stage, totalQuestions } = lifecycle;

  if (stage === "new") {
    return {
      headline: `Hey${name} 👋 First time here?`,
      subline:
        "Let's figure out ProPilot together — ask anything about your shipments in plain English and get instant answers, charts and exports.",
    };
  }

  if (stage === "exploring") {
    return {
      headline: `${hi}${name} 👋`,
      subline: `You've asked ${totalQuestions} question${
        totalQuestions === 1 ? "" : "s"
      } so far — you're getting the hang of this. Try a new topic below to see what else ProPilot can do.`,
    };
  }

  if (stage === "regular") {
    const next = nextMilestone(totalQuestions);
    return {
      headline: `${hi}${name} 👋`,
      subline: next
        ? `You've asked ${totalQuestions} questions — ${next.remaining} away from your next milestone. Keep them coming!`
        : `You've asked ${totalQuestions} questions. Keep them coming!`,
    };
  }

  // power
  return {
    headline: `${hi}${name} — you're a ProPilot power user ⚡`,
    subline: topTopicLabel
      ? `${totalQuestions} questions and counting. ${topTopicLabel} is your home turf — what are we digging into today?`
      : `${totalQuestions} questions and counting. What are we digging into today?`,
  };
}

// ── Wait-state narration ──────────────────────────────────────────────────────
//
// The chat API is request/response (no streaming), so the "what's happening
// behind the scenes" narration is simulated client-side: keyword-match the
// question to a themed stage list. Each stage is { delay (ms), text }.
// If/when the backend ships an SSE endpoint, swap these timers for real events.

const stageSet = (texts) => {
  const delays = [0, 2000, 5000, 10000, 18000, 31000];
  return texts.map((text, i) => ({ delay: delays[i] ?? 31000, text }));
};

const GENERIC_STAGES = stageSet([
  "Reading your question…",
  "Scanning your shipment records…",
  "Crunching the numbers…",
  "Spotting patterns in the data…",
  "Building your answer…",
  "Almost there — wrapping up…",
]);

const FLAVORED_STAGES = {
  ndr: stageSet([
    "Reading your question…",
    "Scanning your shipment records…",
    "Grouping non-delivery reports…",
    "Calculating NDR rates…",
    "Building your view…",
    "Almost there — wrapping up…",
  ]),
  rto: stageSet([
    "Reading your question…",
    "Pulling your return-to-origin shipments…",
    "Tracing where returns originate…",
    "Calculating RTO rates…",
    "Building your view…",
    "Almost there — wrapping up…",
  ]),
  sla: stageSet([
    "Reading your question…",
    "Checking promised vs actual delivery dates…",
    "Measuring SLA adherence…",
    "Ranking breaches by impact…",
    "Building your view…",
    "Almost there — wrapping up…",
  ]),
  carrier: stageSet([
    "Reading your question…",
    "Lining up your carriers side by side…",
    "Comparing delivery performance…",
    "Scoring each carrier…",
    "Building your view…",
    "Almost there — wrapping up…",
  ]),
  pincode: stageSet([
    "Reading your question…",
    "Mapping shipments to pincodes…",
    "Ranking locations by performance…",
    "Highlighting the hotspots…",
    "Building your view…",
    "Almost there — wrapping up…",
  ]),
  payment: stageSet([
    "Reading your question…",
    "Splitting prepaid vs COD shipments…",
    "Comparing how each behaves…",
    "Calculating the split…",
    "Building your view…",
    "Almost there — wrapping up…",
  ]),
};

const FLAVOR_KEYWORDS = [
  { key: "ndr", re: /\bndr\b|non.?deliver/i },
  { key: "rto", re: /\brto\b|return to origin|\breturns?\b/i },
  { key: "sla", re: /\bsla\b|\btat\b|breach|delivery days|on.?time/i },
  { key: "carrier", re: /carrier|courier|delhivery|bluedart|xpressbees|ecom|dtdc|shadowfax/i },
  { key: "pincode", re: /pincode|pin code|city|cities|zone|region|state/i },
  { key: "payment", re: /\bcod\b|prepaid|payment|cash on delivery/i },
];

export function flavorForQuestion(question) {
  const text = (question || "").toString();
  const match = FLAVOR_KEYWORDS.find((f) => f.re.test(text));
  return match ? FLAVORED_STAGES[match.key] : GENERIC_STAGES;
}

// Rotating "did you know" line shown under the skeleton while waiting —
// keeps long waits interesting and teaches the product at the same time.
export const WAIT_TIPS = [
  "Tip: COD orders are roughly 2–3× more likely to RTO than prepaid — worth tracking weekly.",
  "Tip: ask \"…by zone\" or \"…by carrier\" to instantly segment any metric.",
  "Did you know? Calling the buyer within 24h of an NDR dramatically improves re-delivery rates.",
  "Tip: add \"last 30 days\" or \"this month\" to any question to control the time range.",
  "Did you know? A handful of pincodes usually drive a large share of RTOs — find yours.",
  "Tip: after any answer, hit \"Analyze deeper\" for a second layer of insight.",
  "Tip: you can download the exact rows behind any answer — look for the verify section.",
  "Did you know? Address quality is one of the top causes of first-attempt delivery failure.",
];
